import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function BecomeVendor() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ farm_name: "", region: "", story: "", cover_image: "", avatar: "", lat: "", lng: "" });
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="vendor-apply-guest">
        <p className="overline">{t("becomeVendor.overline")}</p>
        <h1 className="font-serif text-5xl tracking-tight mt-2">{t("becomeVendor.guestTitle")}</h1>
        <p className="text-ink-soft mt-6 max-w-lg mx-auto">{t("becomeVendor.guestBody")}</p>
        <button onClick={() => navigate("/login?next=/become-vendor")} className="mt-8 bg-olive text-bone px-8 py-4 rounded-sm text-sm" data-testid="vendor-signin">
          {t("becomeVendor.signInToApply")}
        </button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
      };
      await api.post("/vendors/apply", payload);
      await refresh();
      toast.success("Application submitted — we'll review within 24 hours");
      navigate("/");
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="vendor-apply-page">
      <p className="overline">{t("becomeVendor.appOverline")}</p>
      <h1 className="font-serif text-5xl tracking-tight mt-2">{t("becomeVendor.appTitle")}</h1>
      <p className="text-ink-soft mt-4 max-w-xl">{t("becomeVendor.appBody")}</p>

      <form onSubmit={submit} className="mt-12 space-y-6" data-testid="vendor-apply-form">
        <div>
          <label className="overline">{t("becomeVendor.farmName")}</label>
          <input required value={form.farm_name} onChange={(e) => setForm({ ...form, farm_name: e.target.value })}
            className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm" data-testid="vendor-farm-name" />
        </div>
        <div>
          <label className="overline">{t("becomeVendor.region")}</label>
          <input required value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
            placeholder={t("becomeVendor.regionPlaceholder")}
            className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm" data-testid="vendor-region" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="overline">{t("becomeVendor.latitude")}</label>
            <input type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
              placeholder="e.g. 45.4099"
              className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm" data-testid="vendor-lat" />
          </div>
          <div>
            <label className="overline">{t("becomeVendor.longitude")}</label>
            <input type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })}
              placeholder="e.g. 13.6585"
              className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm" data-testid="vendor-lng" />
          </div>
        </div>
        <div>
          <label className="overline">{t("becomeVendor.story")}</label>
          <textarea required rows={6} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })}
            placeholder={t("becomeVendor.storyPlaceholder")}
            className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm" data-testid="vendor-story" />
        </div>
        <div>
          <label className="overline">{t("becomeVendor.coverImage")}</label>
          <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
            placeholder="https://…"
            className="mt-2 w-full bg-white hairline rounded-sm px-4 py-3 text-sm" data-testid="vendor-cover" />
        </div>
        <button type="submit" disabled={busy} className="bg-olive text-bone px-8 py-4 rounded-sm hover:bg-olive-dark transition-colors text-sm" data-testid="vendor-apply-submit">
          {busy ? t("becomeVendor.submitting") : t("becomeVendor.submit")}
        </button>
      </form>
    </div>
  );
}
